import { useState } from 'react';
import { Button, Checkbox, Icon, Modal, useToast } from '@efira/ui';

import {
  useBindings,
  useGifts,
  useResetGameBindings,
  useResetGameSettings,
  useResetLayout,
  useResetOverlayStyle,
} from '../../../api/queries';
import { applyDictionary } from '../../../bindings/labels';
import { useConsoleSession } from '../../session';
import { GameInfo } from './GameInfo';
import { GameSettings } from './GameSettings';
import { TestActions } from './TestActions';
import { Triggers } from './Triggers';

/**
 * Панель игры в левой колонке консоли.
 *
 * ПОЧЕМУ ЗДЕСЬ, А НЕ ОТДЕЛЬНЫМ ЭКРАНОМ. У эталона всё, чем стример правит
 * игру по ходу эфира, стоит в той же колонке, что и кнопки управления, рядом
 * с кадром (docs/reference/LIVECADE_UX.md, 4). Наш прежний экран привязок жил
 * под `/app/legacy/bindings`: чтобы поменять порог, приходилось уходить с
 * консоли и терять кадр из виду - ровно в тот момент, когда он нужнее всего.
 *
 * Состав повторяет эталон: строка названия со сбросом, «Сведения об игре»,
 * «Привязки действий», «Тестовые действия» (свёрнуты по умолчанию),
 * «Настройки игры».
 *
 * ПРАВИЛА ВИДИМОСТИ, ЗАВЕДЁННЫЕ В ФАЗЕ 3, СОХРАНЯЮТСЯ ЦЕЛИКОМ: условное поле
 * неактивно с причиной, кнопка добавления при исчерпанном наборе видна и
 * неактивна, предел назван числом, счётчик квоты стоит в точке решения, точка
 * входа в редактор выглядит точкой входа (`docs/ARCHITECTURE.md`, 4.2-а).
 */
export function GamePanel() {
  const session = useConsoleSession();
  const gameId = session.gameId;
  const bindings = useBindings(gameId);
  const gifts = useGifts();

  if (gameId === null) {
    return null;
  }

  if (bindings.isLoading || bindings.data === undefined) {
    return (
      <p className="text-foreground/80 px-3 py-2 text-sm">
        {bindings.isError ? 'Панель игры не загрузилась' : 'Панель игры загружается…'}
      </p>
    );
  }

  // Словарь игры приходит с сервера: подписи не зашиты в клиент, и язык
  // добавляется без пересборки dashboard (ARCHITECTURE 4.5).
  applyDictionary(bindings.data.dictionary);

  return (
    <>
      <TitleRow gameId={gameId} title={session.game ?? gameId} />
      <GameInfo />
      <Triggers gameId={gameId} data={bindings.data} gifts={gifts.data} />
      <TestActions gameId={gameId} data={bindings.data} />
      <GameSettings gameId={gameId} data={bindings.data} />
    </>
  );
}

/**
 * Строка названия игры и сброса.
 *
 * СБРОС СПРАШИВАЕТ, ЧТО ИМЕННО СБРАСЫВАТЬ. Три вещи настраиваются порознь -
 * настройки игры, привязки и показ на оверлее, - и одна кнопка «сбросить
 * всё» стоила бы стримеру часа работы над привязками ради возврата одного
 * числа. Флажки сняты не будут по умолчанию: у эталона отмечены все три, и
 * это честно - «сброс» без уточнения значит сброс целиком.
 */
function TitleRow({ gameId, title }: { gameId: string; title: string }) {
  const [open, setOpen] = useState(false);
  const [parts, setParts] = useState({ settings: true, triggers: true, hud: true });
  const toast = useToast();

  const resetSettings = useResetGameSettings(gameId);
  const resetBindings = useResetGameBindings(gameId);
  const resetLayout = useResetLayout(gameId);
  const resetStyle = useResetOverlayStyle(gameId);

  const nothing = !parts.settings && !parts.triggers && !parts.hud;

  const run = async () => {
    const done: string[] = [];

    if (parts.settings) {
      await resetSettings.mutateAsync();
      done.push('настройки игры');
    }

    if (parts.triggers) {
      await resetBindings.mutateAsync();
      done.push('привязки');
    }

    if (parts.hud) {
      await resetLayout.mutateAsync();
      await resetStyle.mutateAsync();
      done.push('показ на оверлее');
    }

    setOpen(false);
    // Ни одна мутация не завершается тихо: тост называет, что именно вернулось.
    toast.show({ tone: 'success', title: `Вернули к заводскому: ${done.join(', ')}` });
  };

  return (
    <>
      <div
        data-control-row="game-title"
        className="border-foreground/10 flex items-center gap-2 overflow-hidden border-b px-3 py-2"
      >
        <span
          data-game="title"
          className="text-foreground/80 min-w-0 flex-1 truncate text-xs font-semibold tracking-wide uppercase"
        >
          {title}
        </span>
        {/* Разрушающее действие с ВЫБОРОМ внутри (что именно сбросить):
            подтверждает окно с отметками, а не Alert Dialog. */}
        <Button
          variant="danger"
          size="s"
          icon={<Icon name="arrowReset" size="sm" />}
          data-game="reset"
          data-destructive="game-reset"
          onClick={() => {
            setOpen(true);
          }}
        >
          Сбросить
        </Button>
      </div>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        title="Вернуть к заводскому"
        confirmId="game-reset"
        actions={
          <>
            <Button
              variant="secondary"
              data-game="reset-cancel"
              onClick={() => {
                setOpen(false);
              }}
            >
              Отмена
            </Button>
            <Button
              variant="danger-solid"
              data-game="reset-confirm"
              data-confirm-action
              disabled={nothing}
              title={nothing ? 'Отметьте хотя бы одно' : undefined}
              onClick={() => {
                void run();
              }}
            >
              Сбросить отмеченное
            </Button>
          </>
        }
      >
        <p className="mb-3 text-sm">
          Ваши правки для игры «{title}» будут стёрты, и вернутся заводские значения. Отметьте, что
          именно сбросить.
        </p>

        <div className="flex flex-col gap-2">
          {(
            [
              ['settings', 'Настройки игры'],
              ['triggers', 'Привязки действий'],
              ['hud', 'Показ на оверлее: раскладка и легенда'],
            ] as const
          ).map(([id, text]) => (
            <label key={id} className="flex items-center gap-2 text-sm">
              <Checkbox
                data-game-reset-part={id}
                checked={parts[id]}
                onCheckedChange={(next) => {
                  setParts((current) => ({ ...current, [id]: next === true }));
                }}
              />
              {text}
            </label>
          ))}
        </div>
      </Modal>
    </>
  );
}
